/**
 * debugInspectUtils — pure helpers for the Debug Session element inspector:
 * a stable CSS selector, a curated computed-style subset, React-fiber → source
 * resolution, and the full element capture. Kept separate from the component so
 * they're unit-testable and the overlay stays lean.
 */
/** A region the app's testing recorder must not keep (SEC-W4-054): left out
 *  of every dev-mode screenshot, an inspected element's HTML, and a click's
 *  recorded text. Spread `CAPTURE_PRIVATE_PROPS` (services/debugService) onto
 *  the region's root. Defined here, not in debugService, because debugService
 *  imports this module and this one imports nothing back. */
export const CAPTURE_PRIVATE_ATTR = 'data-capture-private';

/** Is this element inside a private region? */
export function isInCapturePrivateRegion(el: Element | null | undefined): boolean {
  try {
    return !!el && typeof el.closest === 'function' && !!el.closest(`[${CAPTURE_PRIVATE_ATTR}]`);
  } catch {
    return false;
  }
}

export const PRIVATE_ELEMENT_HTML = '<!-- private region: not captured -->';

export interface CapturedElement {
  tag: string;
  selector: string;
  outerHTML: string;
  rect: { x: number; y: number; width: number; height: number };
  computedStyles: Record<string, string>;
  source_ref: string | null;
}

// The style properties that matter for a design/DOM flag (layout, color,
// typography, spacing) — deliberately curated so the export stays readable.
const STYLE_PROPS = [
  'display', 'position', 'width', 'height', 'margin', 'padding',
  'color', 'background-color', 'font-family', 'font-size', 'font-weight',
  'line-height', 'border', 'border-radius', 'box-shadow', 'flex-direction',
  'gap', 'justify-content', 'align-items', 'z-index', 'opacity', 'text-align',
];

function cssEscapeId(s: string): string {
  return s.replace(/([^\w-])/g, '\\$1');
}

/** A stable-ish selector: id / data-testid short-circuit, else an nth-of-type path. */
export function cssPath(el: Element, maxDepth = 6): string {
  if (!(el instanceof Element)) return '';
  const parts: string[] = [];
  let node: Element | null = el;
  let depth = 0;
  while (node && node.nodeType === 1 && depth < maxDepth) {
    if (node.id) { parts.unshift(`#${cssEscapeId(node.id)}`); break; }
    const testid = node.getAttribute('data-testid');
    if (testid) { parts.unshift(`[data-testid="${testid}"]`); break; }
    let sel = node.tagName.toLowerCase();
    const parent: Element | null = node.parentElement;
    if (parent) {
      const sibs = Array.from(parent.children).filter((c) => c.tagName === node!.tagName);
      if (sibs.length > 1) sel += `:nth-of-type(${sibs.indexOf(node) + 1})`;
    }
    parts.unshift(sel);
    node = node.parentElement;
    depth++;
  }
  return parts.join(' > ');
}

export function computedSubset(el: Element): Record<string, string> {
  const cs = window.getComputedStyle(el as HTMLElement);
  const out: Record<string, string> = {};
  for (const p of STYLE_PROPS) {
    const v = cs.getPropertyValue(p);
    if (v) out[p] = v.trim();
  }
  return out;
}

/** Resolve the nearest React component chain (+ _debugSource file:line when the
 *  build kept it). In production the file:line is usually stripped and names may
 *  be minified — still returns the best breadcrumb available. */
export function reactSourceRef(el: Element): string | null {
  try {
    const key = Object.keys(el).find((k) => k.startsWith('__reactFiber$'));
    if (!key) return null;
    let fiber: any = (el as any)[key];
    const names: string[] = [];
    let src: { fileName?: string; lineNumber?: number } | null = null;
    let guard = 0;
    while (fiber && guard < 40) {
      const t = fiber.type || fiber.elementType;
      let name: string | null = null;
      if (typeof t === 'function') name = t.displayName || t.name || null;
      else if (t && typeof t === 'object') name = t.displayName || t.render?.displayName || t.render?.name || null;
      if (name && name !== 'Unknown' && !names.includes(name)) names.push(name);
      if (!src && fiber._debugSource) src = fiber._debugSource;
      fiber = fiber._debugOwner || fiber.return;
      guard++;
      if (names.length >= 3 && src) break;
    }
    const chain = names.slice(0, 3).join(' ◂ ');
    if (src?.fileName) {
      const file = String(src.fileName).split(/[\\/]/).slice(-2).join('/');
      return `${chain ? `${chain} — ` : ''}${file}:${src.lineNumber ?? ''}`;
    }
    return chain || null;
  } catch {
    return null;
  }
}

export function captureElement(el: Element): CapturedElement {
  const r = el.getBoundingClientRect();
  return {
    tag: el.tagName.toLowerCase(),
    selector: cssPath(el),
    outerHTML: isInCapturePrivateRegion(el) ? PRIVATE_ELEMENT_HTML : (el as HTMLElement).outerHTML.slice(0, 4000),
    rect: { x: Math.round(r.x), y: Math.round(r.y), width: Math.round(r.width), height: Math.round(r.height) },
    computedStyles: computedSubset(el),
    source_ref: reactSourceRef(el),
  };
}

/** Canvas-pixels-per-CSS-pixel for an html2canvas result — used to place pin
 *  markup. Derived from the ACTUAL canvas width rather than the requested
 *  scale, because html2canvas's output scale is what matters (the prod
 *  double-offset bug came from trusting the requested scale while the ctx
 *  also carried a residual transform). */
export function annotationScale(canvasWidth: number, cssWidth: number): number {
  if (!Number.isFinite(canvasWidth) || !Number.isFinite(cssWidth) || canvasWidth <= 0 || cssWidth <= 0) return 1;
  return canvasWidth / cssWidth;
}

/** True when the node belongs to any debug/smoke overlay (excluded from picking). */
export function isOverlayNode(el: Element | null): boolean {
  return !!(el && (el as Element).closest && (el as Element).closest('[data-debug-overlay], [data-debug-inspector], [data-smoke-hud]'));
}
